import { useCallback, useEffect, useMemo, useState } from "react";
import { isHttpError } from "../api/http";
import { useApi } from "../api/api";
import {
  AlertBanner,
  PageTitle,
  Panel,
  Td,
  Th,
  btnDanger,
  btnPrimary,
  btnSecondary,
  inputClass,
  selectClass,
} from "../api/controlPlaneUi";
import {
  ApiProvider,
  CO_ENGINEER_STAGE,
  formatCost,
  ModelCatalogEntry,
  Provider,
  STAGE_COLOR,
  STAGE_LABEL,
  STAGES,
  StageConfig,
} from "../api/types";

// --- Helpers ---------------------------------------------------------------

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const API_PROVIDERS: ApiProvider[] = ["anthropic", "openai", "grok", "other"];

// Sensible per-1M-token price defaults (USD in / out) keyed by apiProvider.
// These only pre-fill the Add form; the user can always override.
const API_PROVIDER_DEFAULTS: Record<ApiProvider, { in: number; out: number }> = {
  anthropic: { in: 15, out: 75 },
  openai: { in: 5, out: 15 },
  grok: { in: 5, out: 15 },
  other: { in: 0, out: 0 },
};

// 401 -> friendly "you must sign in" message; anything else -> generic text.
const messageFor = (err: unknown, fallback: string): string => {
  if (isHttpError(err) && err.status === 401) {
    return "You must be signed in to change configuration.";
  }
  if (err instanceof Error) return `${fallback}: ${err.message}`;
  return fallback;
};

// Shape shared by the add + edit model forms.
interface ModelForm {
  name: string;
  provider: Provider;
  apiProvider: ApiProvider;
  baseUrl: string;
  costIn: string;
  costOut: string;
  tier: string;
  notes: string;
}

const emptyForm: ModelForm = {
  name: "",
  provider: "api",
  apiProvider: "anthropic",
  baseUrl: "",
  costIn: "",
  costOut: "",
  tier: "",
  notes: "",
};

const formFromModel = (m: ModelCatalogEntry): ModelForm => ({
  name: m.name,
  provider: m.provider,
  apiProvider: m.apiProvider ?? "other",
  baseUrl: m.baseUrl ?? "",
  costIn: String(m.costPerMillionTokensIn),
  costOut: String(m.costPerMillionTokensOut),
  tier: m.tier ?? "",
  notes: m.notes ?? "",
});

// Validate a form; returns an error string or null. Builds the request body
// shaped per the contract (apiProvider/baseUrl null'd out as appropriate).
interface ModelBody {
  name: string;
  provider: Provider;
  apiProvider: ApiProvider | null;
  baseUrl: string | null;
  costPerMillionTokensIn: number;
  costPerMillionTokensOut: number;
  tier: string | null;
  notes: string | null;
}

const validateForm = (
  f: ModelForm
): { body: ModelBody } | { error: string } => {
  const name = f.name.trim();
  if (!name) return { error: "Name is required." };

  const costIn = Number(f.costIn === "" ? 0 : f.costIn);
  const costOut = Number(f.costOut === "" ? 0 : f.costOut);
  if (!Number.isFinite(costIn) || costIn < 0) {
    return { error: "Price in must be a number ≥ 0." };
  }
  if (!Number.isFinite(costOut) || costOut < 0) {
    return { error: "Price out must be a number ≥ 0." };
  }

  const notes = f.notes.trim();
  const tier = f.tier && f.tier.trim() ? f.tier.trim().toLowerCase() : null;
  const body: ModelBody = {
    name,
    provider: f.provider,
    apiProvider: f.provider === "api" ? f.apiProvider : null,
    baseUrl: f.provider === "local" ? f.baseUrl.trim() || null : null,
    costPerMillionTokensIn: costIn,
    costPerMillionTokensOut: costOut,
    tier,
    notes: notes || null,
  };
  return { body };
};

// --- Reusable form fields --------------------------------------------------

const labelClass =
  "block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1";

interface FieldsProps {
  form: ModelForm;
  onChange: (patch: Partial<ModelForm>) => void;
}

const ModelFormFields: React.FC<FieldsProps> = ({ form, onChange }) => (
  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
    <div>
      <label className={labelClass}>Name</label>
      <input
        type="text"
        value={form.name}
        onChange={(e) => onChange({ name: e.target.value })}
        placeholder="e.g. claude-opus-4-8"
        className={inputClass}
      />
    </div>
    <div>
      <label className={labelClass}>Provider</label>
      <select
        value={form.provider}
        onChange={(e) => onChange({ provider: e.target.value as Provider })}
        className={selectClass}
      >
        <option value="api">api</option>
        <option value="local">local</option>
      </select>
    </div>
    {form.provider === "api" ? (
      <div>
        <label className={labelClass}>API provider</label>
        <select
          value={form.apiProvider}
          onChange={(e) => {
            const apiProvider = e.target.value as ApiProvider;
            const defaults = API_PROVIDER_DEFAULTS[apiProvider];
            // Pre-fill default prices only when both are still empty so we
            // never clobber a value the user already typed.
            const patch: Partial<ModelForm> = { apiProvider };
            if (form.costIn === "" && form.costOut === "") {
              patch.costIn = String(defaults.in);
              patch.costOut = String(defaults.out);
            }
            onChange(patch);
          }}
          className={selectClass}
        >
          {API_PROVIDERS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>
    ) : (
      <div>
        <label className={labelClass}>Base URL</label>
        <input
          type="text"
          value={form.baseUrl}
          onChange={(e) => onChange({ baseUrl: e.target.value })}
          placeholder="e.g. http://ollama:11434"
          className={inputClass}
        />
      </div>
    )}
    <div>
      <label className={labelClass}>Price in (USD / 1M tokens)</label>
      <input
        type="number"
        min={0}
        step="0.01"
        value={form.costIn}
        onChange={(e) => onChange({ costIn: e.target.value })}
        className={inputClass}
      />
    </div>
    <div>
      <label className={labelClass}>Price out (USD / 1M tokens)</label>
      <input
        type="number"
        min={0}
        step="0.01"
        value={form.costOut}
        onChange={(e) => onChange({ costOut: e.target.value })}
        className={inputClass}
      />
    </div>
    <div>
      <label className={labelClass}>Tier</label>
      <select
        value={form.tier}
        onChange={(e) => onChange({ tier: e.target.value })}
        className={selectClass}
      >
        <option value="">(default / unassigned)</option>
        <option value="low">low</option>
        <option value="medium">medium</option>
        <option value="high">high</option>
        <option value="max">max</option>
      </select>
    </div>
    <div className="md:col-span-2 lg:col-span-1">
      <label className={labelClass}>Notes</label>
      <input
        type="text"
        value={form.notes}
        onChange={(e) => onChange({ notes: e.target.value })}
        placeholder="optional"
        className={inputClass}
      />
    </div>
  </div>
);

// --- Page ------------------------------------------------------------------

const ConfigPage: React.FC = () => {
  const api = useApi();

  const [config, setConfig] = useState<StageConfig[]>([]);
  const [models, setModels] = useState<ModelCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // "saved" affordance per stage (stage -> show check briefly).
  const [savedStage, setSavedStage] = useState<string | null>(null);

  // Add-model form.
  const [addForm, setAddForm] = useState<ModelForm>(emptyForm);

  // Inline edit state (which model id is being edited + its working copy).
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<ModelForm>(emptyForm);

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      const [cfg, mdls] = await Promise.all([api.getConfig(), api.getModels()]);
      setConfig(cfg);
      setModels(mdls);
    } catch (err) {
      setError(messageFor(err, "Failed to load configuration"));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const refreshModels = useCallback(async () => {
    const mdls = await api.getModels();
    setModels(mdls);
  }, [api]);

  // Stage config ordered to match STAGES; tolerates missing entries.
  const configByStage = useMemo(() => {
    const map = new Map<string, StageConfig>();
    config.forEach((c) => map.set(c.stage, c));
    return map;
  }, [config]);

  const modelNames = useMemo(
    () => models.map((m) => m.name).sort(),
    [models]
  );

  const modelByName = useMemo(() => {
    const m = new Map<string, ModelCatalogEntry>();
    models.forEach((mm) => m.set(mm.name, mm));
    return m;
  }, [models]);

  // --- Section 1: stage -> model assignment --------------------------------

  const handleStageModelChange = async (stage: string, model: string) => {
    setError(null);
    try {
      const updated = await api.setStageModel(stage, model);
      setConfig((prev) =>
        prev.map((c) => (c.stage === stage ? updated : c))
      );
      setSavedStage(stage);
      window.setTimeout(
        () => setSavedStage((s) => (s === stage ? null : s)),
        2000
      );
    } catch (err) {
      setError(messageFor(err, "Failed to update stage model"));
    }
  };

  // --- Section 2: model catalog management ---------------------------------

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const result = validateForm(addForm);
    if ("error" in result) {
      setError(result.error);
      return;
    }
    try {
      await api.createModel(result.body);
      setAddForm(emptyForm);
      await refreshModels();
    } catch (err) {
      setError(messageFor(err, "Failed to create model"));
    }
  };

  const startEdit = (m: ModelCatalogEntry) => {
    setError(null);
    setEditId(m.id);
    setEditForm(formFromModel(m));
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditForm(emptyForm);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;
    setError(null);
    const result = validateForm(editForm);
    if ("error" in result) {
      setError(result.error);
      return;
    }
    try {
      await api.updateModel(editId, result.body);
      cancelEdit();
      await refreshModels();
    } catch (err) {
      setError(messageFor(err, "Failed to update model"));
    }
  };

  const handleDelete = async (m: ModelCatalogEntry) => {
    setError(null);
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete model "${m.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await api.deleteModel(m.id);
      if (editId === m.id) cancelEdit();
      await refreshModels();
    } catch (err) {
      setError(messageFor(err, "Failed to delete model"));
    }
  };

  return (
    <div className="space-y-4 max-w-[1400px]">
      <PageTitle kind="LLM Config" name="pipeline-config" />

      {error && <AlertBanner variant="error">{error}</AlertBanner>}

      {loading ? (
        <Panel>
          <p className="text-sm text-slate-500 font-mono">Loading LLM config…</p>
        </Panel>
      ) : (
        <>
          <Panel title="Stage → model bindings">
            <div className="space-y-2">
              {STAGES.map((stage) => {
                const cfg = configByStage.get(stage);
                const current = cfg?.model ?? "";
                const options =
                  current && !modelNames.includes(current)
                    ? [current, ...modelNames]
                    : modelNames;
                const currentEntry = modelByName.get(current);
                const currentTier = currentEntry?.tier ?? null;
                return (
                  <div
                    key={stage}
                    className="grid grid-cols-[auto_140px_1fr_80px_auto] gap-3 items-center py-1.5 border-b border-slate-100 last:border-0"
                  >
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: STAGE_COLOR[stage] }}
                    />
                    <span className="text-xs font-mono text-slate-600 uppercase">
                      {STAGE_LABEL[stage]}
                    </span>
                    <select
                      value={current}
                      onChange={(e) =>
                        handleStageModelChange(stage, e.target.value)
                      }
                      className={selectClass}
                    >
                      {modelNames.length === 0 && (
                        <option value="">no models</option>
                      )}
                      {options.map((name) => {
                        const e = modelByName.get(name);
                        const t = e?.tier ? ` (${e.tier})` : "";
                        return (
                          <option key={name} value={name}>
                            {name}
                            {t}
                          </option>
                        );
                      })}
                    </select>
                    <span
                      className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#262420] text-[#9D9585] text-center"
                      title="model tier"
                    >
                      {currentTier ?? "—"}
                    </span>
                    <span className="text-xs text-slate-400 font-mono">
                      {savedStage === stage
                        ? "applied"
                        : cfg
                          ? formatDate(cfg.updatedAt)
                          : "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          </Panel>

          <Panel title="Co-Engineer">
            {(() => {
              const cfg = configByStage.get(CO_ENGINEER_STAGE);
              const current = cfg?.model ?? "";
              const options =
                current && !modelNames.includes(current)
                  ? [current, ...modelNames]
                  : modelNames;
              return (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 mb-3">
                    Model used when the orchestrator consults a peer engineer via{" "}
                    <span className="font-mono">POST /api/codex/ask</span>.
                  </p>
                  <div className="grid grid-cols-[140px_1fr_80px_auto] gap-3 items-center">
                    <span className="text-xs font-mono text-slate-600 uppercase">Co-Engineer</span>
                    <select
                      value={current}
                      onChange={(e) => handleStageModelChange(CO_ENGINEER_STAGE, e.target.value)}
                      className={selectClass}
                    >
                      {options.length === 0 && <option value="">no models</option>}
                      {options.map((name) => {
                        const e = modelByName.get(name);
                        const t = e?.tier ? ` (${e.tier})` : "";
                        return (
                          <option key={name} value={name}>
                            {name}{t}
                          </option>
                        );
                      })}
                    </select>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#262420] text-[#9D9585] text-center">
                      {modelByName.get(current)?.tier ?? "—"}
                    </span>
                    <span className="text-xs text-slate-400 font-mono">
                      {savedStage === CO_ENGINEER_STAGE
                        ? "applied"
                        : cfg
                          ? formatDate(cfg.updatedAt)
                          : "—"}
                    </span>
                  </div>
                </div>
              );
            })()}
          </Panel>

          <Panel title={`Models (${models.length})`}>
            <div className="overflow-x-auto -mx-4 -mb-4">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Type</Th>
                    <Th>API</Th>
                    <Th>Endpoint</Th>
                    <Th>$ / M in</Th>
                    <Th>$ / M out</Th>
                    <Th>Tier</Th>
                    <Th>Notes</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {models.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-3 py-8 text-center text-sm text-slate-500"
                      >
                        No models registered.
                      </td>
                    </tr>
                  ) : (
                    models.map((m) => (
                      <tr key={m.id} className="hover:bg-[#141210]">
                        <Td mono>{m.name}</Td>
                        <Td mono>{m.provider}</Td>
                        <Td mono>
                          {m.provider === "api" ? m.apiProvider ?? "—" : "—"}
                        </Td>
                        <Td mono>
                          {m.provider === "local" ? m.baseUrl ?? "—" : "—"}
                        </Td>
                        <Td mono>{formatCost(m.costPerMillionTokensIn)}</Td>
                        <Td mono>{formatCost(m.costPerMillionTokensOut)}</Td>
                        <Td mono>{m.tier ?? "—"}</Td>
                        <Td>{m.notes || "—"}</Td>
                        <Td>
                          <button
                            type="button"
                            onClick={() => startEdit(m)}
                            className="text-[#C9A260] hover:underline text-sm mr-3"
                          >
                            edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(m)}
                            className={btnDanger}
                          >
                            delete
                          </button>
                        </Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Panel>

          {editId && (
            <Panel title="Edit model">
              <form onSubmit={handleSaveEdit} className="space-y-4">
                <ModelFormFields
                  form={editForm}
                  onChange={(patch) =>
                    setEditForm((f) => ({ ...f, ...patch }))
                  }
                />
                <div className="flex gap-2">
                  <button type="submit" className={btnPrimary}>
                    Apply
                  </button>
                  <button type="button" onClick={cancelEdit} className={btnSecondary}>
                    Cancel
                  </button>
                </div>
              </form>
            </Panel>
          )}

          <Panel title="Create model">
            <form onSubmit={handleAdd} className="space-y-4">
              <ModelFormFields
                form={addForm}
                onChange={(patch) => setAddForm((f) => ({ ...f, ...patch }))}
              />
              <button type="submit" className={btnPrimary}>
                Create
              </button>
            </form>
          </Panel>
        </>
      )}
    </div>
  );
};

export default ConfigPage;
